import { ChunkDataProvider, MapData, MapId, SceneId } from '../api';
import { getTileSizeForScene } from '../chunkUtils';
import { checkMapCollision } from '../mapCollisionUtils';

/**
 * Service for scene/map data storage.
 * Extracted from State.maps and State.scenes.
 */
export class MapDataService {
  private maps: Partial<Record<MapId, MapData>> = {};
  private scenes: Partial<Record<SceneId, MapId>> = {};
  private chunkProvider?: ChunkDataProvider;

  /**
   * Set the optional chunk data provider.
   */
  public setChunkProvider(provider: ChunkDataProvider): void {
    this.chunkProvider = provider;
  }

  /**
   * Get map data by map ID.
   */
  getMap(mapId: MapId): MapData | undefined {
    return this.maps[mapId];
  }

  /**
   * Set map data for a map ID.
   */
  setMap(mapId: MapId, data: MapData): void {
    this.maps[mapId] = data;
  }

  /**
   * Get all maps.
   */
  getAllMaps(): Partial<Record<MapId, MapData>> {
    return this.maps;
  }

  /**
   * Get the map ID for a scene.
   */
  getSceneMapId(sceneId: SceneId): MapId | undefined {
    return this.scenes[sceneId];
  }

  /**
   * Set the scene to map ID mapping.
   */
  setScene(sceneId: SceneId, mapId: MapId): void {
    this.scenes[sceneId] = mapId;
  }

  /**
   * Get all scene to map mappings.
   */
  getAllScenes(): Partial<Record<SceneId, MapId>> {
    return this.scenes;
  }

  public reset(): void {
    this.maps = {};
    this.scenes = {};
    this.chunkProvider = undefined;
  }

  /**
   * Check if a point is solid (tile-based collision) in a given scene.
   * Returns true if the tile is solid (gid > 0) or out of bounds.
   */
  public isSolid(sceneId: SceneId, x: number, y: number): boolean {
    if (!this.chunkProvider) {
      // Fallback for cases where chunk provider is not yet set (e.g. during early loading)
      const mapId = this.scenes[sceneId];
      if (!mapId) return true;
      const map = this.maps[mapId];
      if (!map || !map.layers['Collisions']) return false;

      const tileSize = getTileSizeForScene(sceneId, this.scenes, this.maps);
      const tileX = Math.floor(x / tileSize);
      const tileY = Math.floor(y / tileSize);

      if (tileX < 0 || tileX >= map.width || tileY < 0 || tileY >= map.height) return true;

      const index = tileY * map.width + tileX;
      const gid = map.layers['Collisions'][index];
      return gid > 0;
    }

    // Use centralized collision logic
    // We check a small 1x1 area around the point
    const result = checkMapCollision(this.chunkProvider, { x, y, width: 1, height: 1 }, sceneId);
    return result !== null;
  }
}
